<template>
    <div id="dashboard-projects-view">
        <span class="dashboard-title mb-5">Project Management</span>
        <span class="dashboard-subtitle mb-10">Manage projects for "{{ account.organization.name }}" organisation</span>

        <template v-if="canCreateProject">
            <div class="d-flex mb-10">
                <v-text-field
                    v-model="form.name"
                    :error-messages="form.$errors.name"
                    label="Create a new project"
                    color="#989898"
                    outlined
                    dark
                    @blur="form.validate('name')"
                    @input="delete form.$errors.name"
                />

                <v-btn class="color-gray rounded-xl ml-10" large outlined @click="addProject">
                    ADD PROJECT
                </v-btn>
            </div>
        </template>


        <span class="dashboard-subtitle mb-4">Existing projects</span>
        <v-row class="font-weight-bold mb-4">
            <v-col sm="3" class="dashboard-column-title">
                Name
            </v-col>
            <v-col sm="3" class="dashboard-column-title text-center">
                Users
            </v-col>
            <v-col sm="3" class="dashboard-column-title text-center">
                Storage
            </v-col>
            <v-col sm="3" class="dashboard-column-title text-center">
                Action
            </v-col>
        </v-row>

        <v-row
            v-for="(project, index) in projects"
            :key="index"
            :class="{ selected: selectedProject == projects[index] }"
            class="list-item-row align-center cursor-pointer mx-n7 mb-6 px-4"
            @mouseover="hover = true"
            @mouseleave="hover = false"
            @click="selectProject(index)"
        >
            <v-col v-if="editingProject !== index" sm="3">
                <span class="dashboard-text">
                    {{ project.name }}
                </span>
            </v-col>

            <v-col v-else sm="3">
                <v-text-field
                    v-model="projectToEdit.name"
                    :ref="`name${index}`"
                    class="dashboard-subtitle color-gray inline-edit-input text-center"
                    dark
                    hide-details
                    @blur="endEditingProject"
                    @keydown="updateProjectName($event, index)"
                />
            </v-col>

            <v-col sm="3" class="text-center">
                {{ project.users.length }}
            </v-col>

            <v-col sm="3" class="text-center">{{ project.storage.toFixed(2) }} MB</v-col>

            <v-col sm="3" class="text-center">
                <v-icon v-if="canRenameProject" @click="startEditingProject(index)" class="color-gray mr-2">mdi
                    mdi-pencil
                </v-icon>
                <v-icon v-if="canDeleteProject" @click="projectToBeDeleted(index)" class="color-gray ml-2">mdi
                    mdi-delete
                </v-icon>
            </v-col>
        </v-row>

        <v-dialog v-model="dialog"
                  content-class="dashboard-dialog"
                  max-width="450px"
                  transition="fade-transition"
        >
            <v-card>
                <v-card-title v-if="projectToDelete">
                    <span>Delete Project "{{ projectToDelete.name }}"?</span>
                </v-card-title>
                <v-card-text class="mb-5">
                    <p class="text-center mb-4">Deleting a project is irreversible. If you are sure, type DELETE in the following box and confirm.</p>
                    <input type="text" v-model="confirmDelete" required />
                </v-card-text>
                <v-card-actions>
                    <v-btn :disabled="confirmDelete != 'DELETE'" class="btn-danger mr-5" text
                           @click="deleteProject(projectToDelete.id)"
                    >
                        Confirm
                    </v-btn>

                    <v-btn class="--cancel" text @click="(dialog = false), (confirmDelete = null)">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import * as rules from "vuelidate/lib/validators";
import CanMixin from "@/mixins/CanMixin";

export default {
    mixins: [CanMixin],

    created() {
        this.$store.dispatch("Projects/fetchProjects");
        this.$store.dispatch("Organizations/fetchUsers");
        this.$store.dispatch("Groups/fetchGroups");
    },

    data() {
        return {
            editingProject: false,
            dialog: false,
            projectToDelete: null,
            projectToEdit: null,
            confirmDelete: null,
            changeName: false,
            hover: false,
            form: new Form(this, "form", {
                name: null,
                account_ids: [],
            }),
        };
    },

    validations: {
        form: {
            name: { required: rules.required },
        },

        projects: {
            name: {
                required: rules.required,
            },
        },
    },

    computed: {
        ...mapState("User", ["account", "user"]),
        ...mapGetters("Projects", {
            projects: "projectsFromActiveOrganisation",
        }),
        ...mapGetters("Projects", ["selectedProject", "projectsFromActiveOrganisation"]),
    },

    methods: {
        selectProject(index) {
            this.$store.commit("Projects/SET_SELECTED_PROJECT", this.projects[index]);
        },

        addProject() {
            if (!this.form.validate()) {
                return;
            }

            this.form.account_ids.push(this.account.id);

            this.$apiService.post(`accounts/${this.account.id}/projects`, this.form).then(response => {
                this.$store.dispatch("Projects/fetchProjects"), (this.form.name = null);
            });
        },

        async deleteProject(id) {
            this.dialog = false;
            this.confirmDelete = null;

            await this.$store.dispatch("Projects/deleteProject", {
                project_id: id,
            });

            this.$store.dispatch("Projects/fetchProjects");
        },

        startEditingProject(index) {
            this.projectToEdit = JSON.parse(JSON.stringify(this.projects[index]));
            this.editingProject = index;

            this.$nextTick(() => {
                this.$refs[`name${index}`][0].$el.querySelector('input').focus();
            })
        },

        endEditingProject() {
            this.editingProject = null;
        },

        updateProjectName(e, index) {
            if (e.key == "Enter" || e.keyCode == 13) {
                this.$apiService
                    .patch(`accounts/${this.account.id}/projects/${this.projects[index].id}`, { name: this.projectToEdit.name })
                    .then(response => {
                        this.endEditingProject();
                        this.$store.dispatch("Projects/fetchProjects");
                    });
            } else if (e.key == "Escape" || e.keyCode == 27) {
                this.endEditingProject();
            }
        },

        projectToBeDeleted(index) {
            this.projectToDelete = this.projects[index];
            this.dialog = true;
        },
    },
};
</script>

<style lang="scss" scoped>
.list-item-row {
    min-height: 60px;
}
</style>